import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate, Link, Outlet, useMatch } from "react-router-dom";
import { bauvorhabenApi, adminApi, invoicesApi, dokumenteApi, einkaufApi, abrechnungApi } from "@/services/api";
import { useAuth } from "@/hooks/useAuth";
import { emitAppEvent, useAppEvent } from "@/utils/appEvents";
import { isOfficeRole, isFinancialRole } from "@/utils/roles";
import type {
  Bauvorhaben, BauvorhabenContact, BauvorhabenContactRole,
  Contact, User, GewerkUpdatePatch, AccordionGewerk, AccordionPayload, BauvorhabenRechnungen,
  BauvorhabenDokumente, BauvorhabenVertragsunterlagen, VertragsunterlagePatch,
  DatenblattPatch, DatenblattResponse, ExtractionApplyPayload, ExtractionApplyResult,
  ExtractionJob, EinkaufResponse, AbrechnungResponse,
} from "@/types";
import { ArrowLeft, Loader2, AlertCircle } from "lucide-react";
import { useOpenContactPanel } from "@/components/sidepanel/registry";
import { BauvorhabenReadView } from "@/components/bauvorhaben/BauvorhabenReadView";
import { BauvorhabenEditForm } from "@/components/bauvorhaben/BauvorhabenEditForm";
import type { BauvorhabenDetailContext } from "@/components/bauvorhaben/BauvorhabenDetailContext";

/**
 * Bauvorhaben-Detail als Layout-Route: besitzt alle Daten und Handler und
 * rendert Leseansicht + Bearbeiten-Spalte (Unter-Route `edit`). Beim
 * Anlegen (/bauvorhaben/new, id === 'new') wird nur das Formular
 * gerendert — keine Leseansicht, keine Assoziationen, kein Outlet.
 */

// Tage zwischen zwei reinen Datumswerten (YYYY-MM-DD), NULL-sicher.
function daysBetweenDates(from: string | null, to: string | null): number | null {
  if (!from || !to) return null;
  return Math.round((Date.parse(`${to}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`)) / 86400000);
}

/** Patch in den Gewerk-Eintrag mischen und die abgeleiteten Dauer-Felder
 *  client-seitig nachrechnen (Server liefert sie nur beim Load). */
function applyGewerkPatch(gewerk: AccordionGewerk, patch: GewerkUpdatePatch): AccordionGewerk {
  const merged: AccordionGewerk = { ...gewerk, ...patch };
  merged.dauer_tage = daysBetweenDates(merged.beauftragt_am, merged.fertiggestellt_am);
  merged.tage_seit_beauftragt = merged.fertiggestellt_am
    ? null
    : daysBetweenDates(merged.beauftragt_am, new Date().toLocaleDateString('sv-SE', { timeZone: 'Europe/Berlin' }));
  return merged;
}

export function BauvorhabenDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user, isApprover, hasModule } = useAuth();
  const openContactInPanel = useOpenContactPanel();
  const isNew = id === 'new';
  const editOpen = useMatch('/bauvorhaben/:id/edit') !== null;

  // Kontakt-Links öffnen die Split-View-Seitenleiste statt zu navigieren;
  // Modifikator-Klick / mittlere Maustaste bleibt natives Browser-Verhalten.
  const openContactFromLink = (e: React.MouseEvent, contactId: number, name?: string) => {
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;
    e.preventDefault();
    openContactInPanel(contactId, name);
  };

  const [bauvorhaben, setBauvorhaben] = useState<Bauvorhaben | null>(null);
  const [contacts, setContacts] = useState<BauvorhabenContact[]>([]);
  const [isLoading, setIsLoading] = useState(!isNew);
  const [error, setError] = useState('');
  const [saveError, setSaveError] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [users, setUsers] = useState<User[]>([]);

  const [formData, setFormData] = useState({
    name: '',
    project_number: '',
    street: '',
    postal_code: '',
    city: '',
    abgebrochen: false,
    start_date: '',
    end_date: '',
    bauleiter_user_id: '',
    notes: '',
  });

  // Vertriebler-Auswahl im Formular: {id, name} oder null (kein Vertriebler)
  const [vertriebler, setVertriebler] = useState<{ id: number; name: string } | null>(null);

  // Gewerk-Checkliste (Akkordeon) — gesamter aktiver Katalog
  const [accordion, setAccordion] = useState<AccordionPayload | null>(null);

  // Vertragsunterlagen-Checkliste (A-Checkliste-MWb-Ersatz)
  const [vertragsunterlagen, setVertragsunterlagen] = useState<BauvorhabenVertragsunterlagen | null>(null);

  // Datenblatt (Phase 2): app-natives „_Datenblatt_“-Formular
  const [datenblatt, setDatenblatt] = useState<DatenblattResponse | null>(null);

  // KI-Extraktion (Phase 2): Job-Liste, geöffneter Job (inkl. Ergebnis
  // fürs Review), Start-/Apply-Fehler + -Ergebnis, Poll-Fenster.
  const [extraktionen, setExtraktionen] = useState<ExtractionJob[] | null>(null);
  const [activeExtraction, setActiveExtraction] = useState<ExtractionJob | null>(null);
  const [isStarting, setIsStarting] = useState(false);
  const [startError, setStartError] = useState('');
  const [applyResult, setApplyResult] = useState<ExtractionApplyResult | null>(null);
  const [applyError, setApplyError] = useState('');
  const [pollStartedAt, setPollStartedAt] = useState<number | null>(null);

  // Finanzen: letzte Rechnungen + Summen (Karte in der Leseansicht)
  const [rechnungen, setRechnungen] = useState<BauvorhabenRechnungen | null>(null);

  // Einkauf (Phase 3): EK-Ebene-Zeilen + Summen. Lesen: Office-Rollen ODER
  // BV-Bauleiter; Vertrieb rendert nur die Upload-Box und bekamme NIEMALS
  // einen Fetch (Server 403 — vermeiden, nicht abfangen).
  const [einkauf, setEinkauf] = useState<EinkaufResponse | null>(null);
  const canEinkauf =
    isOfficeRole(user?.role) ||
    (user?.role === 'bauleiter' && !!bauvorhaben && bauvorhaben.bauleiter_user_id === user.id);
  const canWriteEinkauf = isFinancialRole(user?.role);
  const [einkaufReloadKey, setEinkaufReloadKey] = useState(0);
  useAppEvent('einkauf:changed', () => setEinkaufReloadKey((k) => k + 1));
  const reloadEinkauf = useCallback(() => setEinkaufReloadKey((k) => k + 1), []);
  useEffect(() => {
    if (isNew || !canEinkauf || !bauvorhaben) return;
    let cancelled = false;
    einkaufApi
      .get(Number(id))
      .then((response) => {
        if (!cancelled) setEinkauf(response.data.data);
      })
      .catch(() => {
        if (!cancelled) setEinkauf(null);
      });
    return () => {
      cancelled = true;
    };
  }, [id, isNew, canEinkauf, bauvorhaben, einkaufReloadKey]);

  // Abrechnung (Phase 3): gleiche Lesenden wie Einkauf; Formelkette
  // hängt an der Herstellsumme → auch bei 'einkauf:changed' neu laden.
  const [abrechnung, setAbrechnung] = useState<AbrechnungResponse | null>(null);
  useEffect(() => {
    if (isNew || !canEinkauf || !bauvorhaben) return;
    let cancelled = false;
    abrechnungApi
      .get(Number(id))
      .then((response) => {
        if (!cancelled) setAbrechnung(response.data.data);
      })
      .catch(() => {
        if (!cancelled) setAbrechnung(null);
      });
    return () => {
      cancelled = true;
    };
  }, [id, isNew, canEinkauf, bauvorhaben, einkaufReloadKey]);

  // Dokumente-Karte — nur für Nutzer mit beiden Modulen (bauvorhaben +
  // dokumente); ohne dokumente-Modul: keine Karte, kein Request, keine 403.
  const canDokumente = hasModule('dokumente');
  const [dokumente, setDokumente] = useState<BauvorhabenDokumente | null>(null);

  const [associationError, setAssociationError] = useState('');
  // Positive Rückmeldung (z. B. erzeugte Adress-Anträge für Bauherren)
  const [associationInfo, setAssociationInfo] = useState('');

  const loadDetail = useCallback(() => {
    if (isNew) return;
    setIsLoading(true);
    bauvorhabenApi
      .getById(Number(id))
      .then((response) => {
        setBauvorhaben(response.data.data.bauvorhaben);
        setContacts(response.data.data.contacts);
        setFormData({
          name: response.data.data.bauvorhaben.name ?? '',
          project_number: response.data.data.bauvorhaben.project_number ?? '',
          street: response.data.data.bauvorhaben.street ?? '',
          postal_code: response.data.data.bauvorhaben.postal_code ?? '',
          city: response.data.data.bauvorhaben.city ?? '',
          // Der Override überlebt die Status-Derivation im Backend — der
          // Vergleich mit 'abgebrochen' stellt das klebrige Flag korrekt her.
          abgebrochen: response.data.data.bauvorhaben.status === 'abgebrochen',
          start_date: response.data.data.bauvorhaben.start_date ?? '',
          end_date: response.data.data.bauvorhaben.end_date ?? '',
          bauleiter_user_id: response.data.data.bauvorhaben.bauleiter_user_id
            ? String(response.data.data.bauvorhaben.bauleiter_user_id)
            : '',
          notes: response.data.data.bauvorhaben.notes ?? '',
        });
        setVertriebler(
          response.data.data.bauvorhaben.vertriebler_contact_id
            ? {
                id: response.data.data.bauvorhaben.vertriebler_contact_id,
                name: response.data.data.bauvorhaben.vertriebler_name || 'Kontakt',
              }
            : null
        );
      })
      .catch(() => setError('Bauvorhaben nicht gefunden'))
      .finally(() => setIsLoading(false));
  }, [id, isNew]);

  useEffect(() => {
    loadDetail();
  }, [loadDetail]);

  // Finanzen-Karte: eigenen Fetch + Reload bei Rechnungs-Änderungen
  // (Statuswechsel/Neulanlage passieren in der Rechnungen-Spalte).
  // Ohne 'rechnungen'-Modul (z. B. Vertrieb): kein Request, keine Karte.
  const canRechnungen = hasModule('rechnungen');
  const [rechnungenReloadKey, setRechnungenReloadKey] = useState(0);
  useAppEvent('rechnungen:changed', () => setRechnungenReloadKey((k) => k + 1));
  useEffect(() => {
    if (isNew || !canRechnungen) return;
    let cancelled = false;
    invoicesApi
      .getByBauvorhaben(Number(id))
      .then((response) => {
        if (!cancelled) setRechnungen(response.data.data);
      })
      .catch(() => {
        if (!cancelled) setRechnungen(null);
      });
    return () => {
      cancelled = true;
    };
  }, [id, isNew, canRechnungen, rechnungenReloadKey]);

  // Dokumente-Karte: eigener Fetch + Reload bei dokumente:changed
  // (Upload/Löschen passieren in der Karte selbst).
  const [dokumenteReloadKey, setDokumenteReloadKey] = useState(0);
  useAppEvent('dokumente:changed', () => setDokumenteReloadKey((k) => k + 1));
  useEffect(() => {
    if (isNew || !canDokumente) return;
    let cancelled = false;
    dokumenteApi
      .getByBauvorhaben(Number(id))
      .then((response) => {
        if (!cancelled) setDokumente(response.data.data);
      })
      .catch(() => {
        if (!cancelled) setDokumente(null);
      });
    return () => {
      cancelled = true;
    };
  }, [id, isNew, canDokumente, dokumenteReloadKey]);

  // Gewerk-Checkliste laden (nur im Detail-Modus)
  useEffect(() => {
    if (isNew) return;
    let cancelled = false;
    bauvorhabenApi
      .getGewerke(Number(id))
      .then((response) => {
        if (!cancelled) setAccordion(response.data.data);
      })
      .catch(() => {
        if (!cancelled) setAccordion(null);
      });
    return () => {
      cancelled = true;
    };
  }, [id, isNew]);

  // Vertragsunterlagen-Checkliste laden (nur im Detail-Modus)
  useEffect(() => {
    if (isNew) return;
    let cancelled = false;
    bauvorhabenApi
      .getVertragsunterlagen(Number(id))
      .then((response) => {
        if (!cancelled) setVertragsunterlagen(response.data.data);
      })
      .catch(() => {
        if (!cancelled) setVertragsunterlagen(null);
      });
    return () => {
      cancelled = true;
    };
  }, [id, isNew]);

  // Datenblatt (Phase 2) laden — nur im Detail-Modus
  useEffect(() => {
    if (isNew) return;
    let cancelled = false;
    bauvorhabenApi
      .getDatenblatt(Number(id))
      .then((response) => {
        if (!cancelled) setDatenblatt(response.data.data);
      })
      .catch(() => {
        if (!cancelled) setDatenblatt(null);
      });
    return () => {
      cancelled = true;
    };
  }, [id, isNew]);

  // KI-Extraktionen (Phase 2) laden — Liste (ohne Ergebnisse)
  useEffect(() => {
    if (isNew) return;
    let cancelled = false;
    bauvorhabenApi
      .getExtraktionen(Number(id))
      .then((response) => {
        if (!cancelled) setExtraktionen(response.data.data);
      })
      .catch(() => {
        if (!cancelled) setExtraktionen(null);
      });
    return () => {
      cancelled = true;
    };
  }, [id, isNew]);

  // Poll (Phase 2): geöffneter Job wartet/läuft → alle 4 s den Stand
  // holen, Cap 10 Minuten (OCR von Scans kann dauern — danach manueller
  // Refresh über den Button). Ende-status beendet das Intervall.
  const pollActive =
    !!activeExtraction &&
    (activeExtraction.status === 'pending' || activeExtraction.status === 'processing') &&
    (pollStartedAt === null || Date.now() - pollStartedAt < 10 * 60 * 1000);
  useEffect(() => {
    if (isNew || !activeExtraction || !pollActive) return;
    const jobId = activeExtraction.id;
    const timer = setInterval(() => {
      bauvorhabenApi
        .getExtraction(Number(id), jobId)
        .then((response) => {
          const job = response.data.data;
          setActiveExtraction((prev) => (prev && prev.id === job.id ? job : prev));
          setExtraktionen((prev) =>
            prev ? prev.map((j) => (j.id === job.id ? { ...j, ...job } : j)) : prev
          );
          if (job.status === 'done' || job.status === 'failed') {
            clearInterval(timer);
          }
        })
        .catch(() => {
          /* transient — der nächste Tick versucht es erneut */
        });
    }, 4000);
    return () => clearInterval(timer);
    // pollStartedAt bewusst nicht im Array: Cap ändert nur die Anzeige.
  }, [id, isNew, pollActive, activeExtraction?.id]);

  // Benutzerliste für den Bauleiter-Select — nötig im Anlege- und Bearbeiten-Modus
  const needsUsers = isNew || editOpen;
  useEffect(() => {
    if (!needsUsers || !isApprover || users.length > 0) return;
    adminApi
      .getUsers()
      .then((response) => setUsers(response.data.data))
      .catch(() => setUsers([]));
  }, [needsUsers, isApprover, users.length]);

  const handleSave = () => {
    setSaveError('');
    if (!formData.name.trim()) {
      setSaveError('Bitte einen Namen angeben');
      return;
    }
    setIsSaving(true);
    const payload = {
      name: formData.name.trim(),
      project_number: formData.project_number.trim() || null,
      street: formData.street.trim() || null,
      postal_code: formData.postal_code.trim() || null,
      city: formData.city.trim() || null,
      abgebrochen: formData.abgebrochen,
      start_date: formData.start_date || null,
      end_date: formData.end_date || null,
      bauleiter_user_id: formData.bauleiter_user_id ? Number(formData.bauleiter_user_id) : null,
      vertriebler_contact_id: vertriebler?.id ?? null,
      notes: formData.notes.trim() || null,
    };
    const request = isNew
      ? bauvorhabenApi.create(payload)
      : bauvorhabenApi.update(Number(id), payload);

    request
      .then((response) => {
        // Gemountete Listen-Spalte still nachladen (Create erscheint sofort).
        emitAppEvent('bauvorhaben:changed');
        if (isNew) {
          // Gleiche Layoutroute, neuer :id — Element bleibt gemountet,
          // loadDetail feuert über den id-Wechsel neu.
          navigate(`/bauvorhaben/${(response.data.data as { id: number }).id}`, { replace: true });
        } else {
          const created = (response.data.data as { address_proposals_created?: number }).address_proposals_created;
          if (created && created > 0) {
            setAssociationInfo(
              `Für ${created} Bauherr(en) wurde ein Antrag zur Übernahme der Bauvorhaben-Adresse erstellt — er muss noch genehmigt werden.`
            );
          }
          navigate(`/bauvorhaben/${id}`);
          loadDetail();
        }
      })
      .catch((err) => {
        setSaveError(err.response?.data?.error || 'Fehler beim Speichern');
      })
      .finally(() => setIsSaving(false));
  };

  const handleCancel = () => {
    if (isNew) {
      navigate('/bauvorhaben');
    } else {
      navigate(`/bauvorhaben/${id}`);
      loadDetail();
    }
  };

  const handleDelete = () => {
    if (!window.confirm('Möchten Sie dieses Bauvorhaben wirklich löschen?')) return;
    bauvorhabenApi
      .remove(Number(id))
      .then(() => {
        emitAppEvent('bauvorhaben:changed');
        navigate('/bauvorhaben');
      })
      .catch((err) => setError(err.response?.data?.error || 'Fehler beim Löschen'));
  };

  const handleAddContact = (role: BauvorhabenContactRole, contact: Contact) => {
    if (isNew) return;
    setAssociationError('');
    setAssociationInfo('');
    bauvorhabenApi
      .addContact(Number(id), {
        contact_id: contact.id,
        role,
      })
      .then((res) => {
        const created = res.data.data.address_proposals_created;
        if (created && created > 0) {
          setAssociationInfo(
            `Für ${created} Bauherr(en) wurde ein Antrag zur Übernahme der Bauvorhaben-Adresse erstellt — er muss noch genehmigt werden.`
          );
        }
        // Bauherren-Zähler in der Listen-Spalte mitziehen.
        emitAppEvent('bauvorhaben:changed');
        loadDetail();
      })
      .catch((err) => {
        setAssociationError(err.response?.data?.error || 'Fehler beim Zuordnen');
      });
  };

  const handleRemoveContact = (linkId: number) => {
    if (isNew) return;
    setAssociationError('');
    bauvorhabenApi
      .removeContact(Number(id), linkId)
      .then(() => {
        emitAppEvent('bauvorhaben:changed');
        loadDetail();
      })
      .catch((err) => {
        setAssociationError(err.response?.data?.error || 'Fehler beim Entfernen');
      });
  };

  // Gewerk patchen (Status + Prozess-Daten, Bautenstandsliste): optimistisch
  // im Akkordeon-State, Reload bei Fehler. Abgeleitete Dauer-Felder werden
  // client-seitig nachgerechnet (Server liefert sie nur beim Load).
  const handleGewerkUpdate = async (gewerkId: number, patch: GewerkUpdatePatch) => {
    if (isNew || !accordion) return;
    const previous = accordion;
    const updated: AccordionPayload = {
      ...accordion,
      phases: accordion.phases.map((phase) => ({
        ...phase,
        gewerke: phase.gewerke.map((gewerk) =>
          gewerk.id === gewerkId ? applyGewerkPatch(gewerk, patch) : gewerk
        ),
      })),
    };
    setAccordion(updated);
    try {
      await bauvorhabenApi.updateGewerk(Number(id), gewerkId, patch);
      // Fortschrittsbalken/Status der Listen-Zeile mitziehen.
      emitAppEvent('bauvorhaben:changed');
    } catch (err) {
      setAccordion(previous);
      setAssociationError((err as { response?: { data?: { error?: string } } })?.response?.data?.error || 'Fehler beim Aktualisieren des Gewerk-Status');
    }
  };

  // Vertragsunterlage patchen (A-Checkliste-Ersatz): optimistisch,
  // Zurücksetzen bei Fehler. complete_count client-seitig nachrechnen.
  const handleVertragsunterlageUpdate = async (itemKey: string, patch: VertragsunterlagePatch) => {
    if (isNew || !vertragsunterlagen) return;
    const previous = vertragsunterlagen;
    const updated: BauvorhabenVertragsunterlagen = {
      ...vertragsunterlagen,
      items: vertragsunterlagen.items.map((item) =>
        item.item_key === itemKey ? { ...item, ...patch } : item
      ),
    };
    updated.complete_count = updated.items.filter((i) => i.status === 'vollständig').length;
    setVertragsunterlagen(updated);
    try {
      await bauvorhabenApi.updateVertragsunterlage(Number(id), itemKey, patch);
    } catch (err) {
      setVertragsunterlagen(previous);
      setAssociationError((err as { response?: { data?: { error?: string } } })?.response?.data?.error || 'Fehler beim Aktualisieren der Vertragsunterlage');
    }
  };

  // Datenblatt (Phase 2) speichern: Partial-Put der geänderten Felder,
  // Antwort = neuer Stand (ersetzt den lokalen State komplett).
  const handleDatenblattSave = async (patch: DatenblattPatch) => {
    try {
      const response = await bauvorhabenApi.updateDatenblatt(Number(id), patch);
      setDatenblatt(response.data.data);
    } catch (err) {
      setAssociationError((err as { response?: { data?: { error?: string } } })?.response?.data?.error || 'Fehler beim Speichern des Datenblatts');
    }
  };

  const loadExtraktionen = useCallback(() => {
    if (isNew) return;
    bauvorhabenApi
      .getExtraktionen(Number(id))
      .then((response) => setExtraktionen(response.data.data))
      .catch(() => setExtraktionen(null));
  }, [id, isNew]);

  // KI-Extraktion starten (Phase 2): Job anlegen, sofort beobachten.
  const handleStartExtraction = (documentId: number) => {
    setStartError('');
    setIsStarting(true);
    bauvorhabenApi
      .startExtraction(Number(id), documentId)
      .then((response) => {
        setActiveExtraction(response.data.data);
        setApplyResult(null);
        setApplyError('');
        setPollStartedAt(Date.now());
        loadExtraktionen();
      })
      .catch((err) => {
        setStartError(err.response?.data?.error || 'Extraktion konnte nicht gestartet werden');
      })
      .finally(() => setIsStarting(false));
  };

  // Job öffnen: fertige/fehlgeschlagene Jobs mit Ergebnis via show
  // laden (die Liste enthält kein result_json).
  const handleSelectExtraction = (job: ExtractionJob) => {
    setApplyResult(null);
    setApplyError('');
    const running = job.status === 'pending' || job.status === 'processing';
    setPollStartedAt(running ? Date.now() : null);
    if (job.status === 'done' || job.status === 'failed') {
      bauvorhabenApi
        .getExtraction(Number(id), job.id)
        .then((response) => setActiveExtraction(response.data.data))
        .catch(() => setActiveExtraction(job));
    } else {
      setActiveExtraction(job);
    }
  };

  // Geprüftes Extraktionsergebnis übernehmen (approver+): Datenblatt +
  // Bauadresse direkt, Bauherr-Kontaktdaten als Anträge. Danach alles
  // nachladen — Datenblatt, Job-Liste (applied_at) und die BV-Stammdaten
  // (Apply kann die BV-Adresse geschrieben haben).
  const handleApplyExtraction = async (jobId: number, payload: ExtractionApplyPayload) => {
    setApplyError('');
    try {
      const response = await bauvorhabenApi.applyExtraction(Number(id), jobId, payload);
      setApplyResult(response.data.data);
      bauvorhabenApi
        .getDatenblatt(Number(id))
        .then((r) => setDatenblatt(r.data.data))
        .catch(() => setDatenblatt(null));
      loadExtraktionen();
      loadDetail();
      emitAppEvent('bauvorhaben:changed');
    } catch (err) {
      setApplyError((err as { response?: { data?: { error?: string } } })?.response?.data?.error || 'Fehler beim Übernehmen des Extraktionsergebnisses');
    }
  };

  // Gewerk-Kontakte an/ab hängen: erzeugt nur bauvorhaben_gewerke-Zeilen
  // mit Default-Status 'zu_erledigen' — Fortschritt/Status in der Liste
  // bleiben unverändert, deshalb KEIN emitAppEvent hier.
  const handleGewerkAddContact = async (gewerkId: number, contact: Contact) => {
    if (isNew) return;
    setAssociationError('');
    try {
      const res = await bauvorhabenApi.addGewerkContact(Number(id), gewerkId, contact.id);
      const linkId = res.data.data.link_id;
      setAccordion((prev) => prev && ({
        ...prev,
        phases: prev.phases.map((phase) => ({
          ...phase,
          gewerke: phase.gewerke.map((gewerk) =>
            gewerk.id === gewerkId
              ? {
                  ...gewerk,
                  contacts: [
                    ...gewerk.contacts,
                    {
                      link_id: linkId,
                      contact_id: contact.id,
                      display_name: contact.display_name,
                      first_name: contact.first_name,
                      last_name: contact.last_name,
                      company_name: contact.company_name,
                      email_address: contact.email_address,
                      business_phone: contact.business_phone,
                    },
                  ],
                }
              : gewerk
          ),
        })),
      }));
    } catch (err) {
      setAssociationError((err as { response?: { data?: { error?: string } } })?.response?.data?.error || 'Fehler beim Zuordnen');
    }
  };

  const handleGewerkRemoveContact = async (gewerkId: number, linkId: number) => {
    if (isNew) return;
    setAssociationError('');
    try {
      await bauvorhabenApi.removeGewerkContact(Number(id), gewerkId, linkId);
      setAccordion((prev) => prev && ({
        ...prev,
        phases: prev.phases.map((phase) => ({
          ...phase,
          gewerke: phase.gewerke.map((gewerk) =>
            gewerk.id === gewerkId
              ? { ...gewerk, contacts: gewerk.contacts.filter((c) => c.link_id !== linkId) }
              : gewerk
          ),
        })),
      }));
    } catch (err) {
      setAssociationError((err as { response?: { data?: { error?: string } } })?.response?.data?.error || 'Fehler beim Entfernen');
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin text-primary-600" />
      </div>
    );
  }

  if (error && !bauvorhaben && !isNew) {
    return (
      <div className="p-4">
        <div className="card p-8 text-center">
          <AlertCircle className="mx-auto mb-3 h-8 w-8 text-red-500" />
          <p className="text-gray-700 mb-4">{error}</p>
          <Link to="/bauvorhaben" className="btn btn-secondary inline-flex items-center gap-2">
            <ArrowLeft className="h-4 w-4" />
            Zurück zur Übersicht
          </Link>
        </div>
      </div>
    );
  }

  const context: BauvorhabenDetailContext = {
    bauvorhaben,
    contacts,
    accordion,
    rechnungen,
    canRechnungen,
    dokumente,
    canDokumente,
    reloadDokumente: () => setDokumenteReloadKey((k) => k + 1),
    formData,
    setFormData,
    vertriebler,
    setVertriebler,
    users,
    isApprover,
    isNew,
    isSaving,
    saveError,
    associationError,
    associationInfo,
    save: handleSave,
    cancel: handleCancel,
    remove: handleDelete,
    addContact: handleAddContact,
    removeContact: handleRemoveContact,
    updateGewerk: handleGewerkUpdate,
    canEditGewerke: isApprover || (!!user && !!bauvorhaben && bauvorhaben.bauleiter_user_id === user.id),
    vertragsunterlagen,
    canEditVertragsunterlagen: isApprover || (!!user && user.role === 'vertrieb'),
    updateVertragsunterlage: handleVertragsunterlageUpdate,
    // Datenblatt + KI-Extraktion (Phase 2). Schreiben = approver+ oder
    // Vertrieb am eigenen BV (Server scoped vertriebs-Zugriff aufs BV);
    // Übernehmen = approver+ (schreibt Datenblatt + BV-Adresse).
    datenblatt,
    saveDatenblatt: handleDatenblattSave,
    canEditDatenblatt: isApprover || (!!user && user.role === 'vertrieb'),
    extraktionen,
    activeExtraction,
    extractionPolling: pollActive,
    extractionPollCapped:
      pollStartedAt !== null && Date.now() - pollStartedAt >= 10 * 60 * 1000,
    isStartingExtraction: isStarting,
    startExtractionError: startError,
    extractionApplyResult: applyResult,
    extractionApplyError: applyError,
    startExtraction: handleStartExtraction,
    refreshExtraktionen: loadExtraktionen,
    selectExtraction: handleSelectExtraction,
    applyExtraction: handleApplyExtraction,
    dismissApplyResult: () => setApplyResult(null),
    canApplyExtraction: isApprover,
    canPickContacts: hasModule('kontakte'),
    addGewerkContact: handleGewerkAddContact,
    removeGewerkContact: handleGewerkRemoveContact,
    // Einkauf (Phase 3): EK-Ebene-Zeilen + Extraktion (Karten in ReadView).
    einkauf,
    canEinkauf,
    canWriteEinkauf,
    reloadEinkauf,
    // Abrechnung (Phase 3): gleiche Lesenden, gleiche Schreibrechte wie
    // Einkauf; reloadAnreiz über einkauf:changed (Herstellsumme).
    abrechnung,
    setAbrechnung,
    reloadAbrechnung: reloadEinkauf,
    openContactFromLink,
    reload: loadDetail,
  };

  // Anlegen: nur das Formular in der Detail-Spalte
  if (isNew) {
    return (
      <div className="p-4">
        <Link to="/bauvorhaben" className="mb-4 inline-flex items-center gap-1 text-sm text-gray-500 hover:text-gray-800">
          <ArrowLeft className="h-4 w-4" />
          Bauvorhaben
        </Link>
        <div className="card p-6">
          <BauvorhabenEditForm context={context} />
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-full flex-col lg:flex-row">
      {/* Leseansicht */}
      <section className="min-w-0 flex-1 p-4">
        <BauvorhabenReadView context={context} />
      </section>
      {/* Bearbeiten-Spalte (Route /bauvorhaben/:id/edit) */}
      <section
        className={
          editOpen
            ? 'min-w-0 shrink-0 p-4 lg:w-[440px] lg:border-l lg:border-gray-200 lg:pl-6'
            : 'hidden'
        }
      >
        <Outlet context={context} />
      </section>
    </div>
  );
}